import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronLeft } from "lucide-react";
import React, { useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { Load } from "components/load";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { toast } from "components/ui/use-toast";
import { PageLayout } from "components/layout";

import { CampaignType, type IStore } from "../../../../@types/campaign-roulette";
import {
  useGetDefaultProbabilitiesQuery,
  useLazyValidateCampaignPeriodQuery,
  useUpdateCampaignRouletteMutation,
} from "../../../../services/campaign-roulette";
import {
  useGetStoresOptionsWithDetailsQuery,
  type IStoreOptionWithDetails
} from "../../../../services/store/select-options";
import {
  STORE_OPTION_VALUES,
} from "../constants/store-options";
import { calculateTotalProbability } from "../utils/probability-helpers";
import { transformProbabilitiesToPercentage } from "../utils/probability-utils";
import { getStatusColor, getStatusDescription, isCampaignActive, isCampaignEditable } from "../utils/status";
import { formatStoreAddress, formatStoreNickname } from "../utils/store-formatters";
import {
  updateCampaignRouletteSchema,
  type UpdateCampaignRouletteFormData,
} from "../validations/campaign-roulette.validation";
import { BannersCard } from "./components/BannersCard";
import { BasicInfoCard } from "./components/BasicInfoCard";
import { ProbabilitiesCard } from "./components/ProbabilitiesCard";
import { RegulationsCard } from "./components/RegulationsCard";
import { StoresCard } from "./components/StoresCard";
import { useBannerState } from "./hooks/useBannerState";
import { useCampaignData } from "./hooks/useCampaignData";
import { useFormSubmit } from "./hooks/useFormSubmit";
import { useLinkedCampaignSearch } from "../hooks/useLinkedCampaignSearch";
import { buildEditCampaignFormValues } from "./utils/build-edit-form-values";

export const EditCampaignRoulette: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const campaignId = parseInt(id || "0", 10);

  const [updateCampaign, { isLoading: isUpdating }] =
    useUpdateCampaignRouletteMutation();
  const [validatePeriod] = useLazyValidateCampaignPeriodQuery();
  const { campaign, isLoading, error } = useCampaignData(campaignId);
  const { data: defaultProbabilities = [] } = useGetDefaultProbabilitiesQuery();
  const {
    data: storeOptions = [],
    isLoading: isLoadingStores,
    isFetching: isFetchingStores,
  } = useGetStoresOptionsWithDetailsQuery();
  const [storeCodesInput, setStoreCodesInput] = useState("");
  const linkedCampaignSearch = useLinkedCampaignSearch();
  const [linkedCampaignNameOverride, setLinkedCampaignNameOverride] =
    useState<string | null | undefined>(undefined);

  const selectedLinkedCampaignName =
    linkedCampaignNameOverride !== undefined
      ? linkedCampaignNameOverride
      : campaign?.linkedCampaignName ?? null;

  const formValues = useMemo(
    () => (campaign ? buildEditCampaignFormValues(campaign) : undefined),
    [campaign]
  );

  const form = useForm<UpdateCampaignRouletteFormData>({
    resolver: zodResolver(updateCampaignRouletteSchema),
    defaultValues: {
      id: campaignId,
      campaignName: "",
      campaignType: CampaignType.ROULETTE,
      startDate: "",
      endDate: "",
      stores: [],
      probabilities: [],
      regulations: undefined,
      allStoresSelected: false,
    },
    values: formValues,
  });

  const { fields: probabilityFields, append: appendProbability } =
    useFieldArray({
      control: form.control,
      name: "probabilities",
    });

  const {
    fields: storeFields,
    append: appendStore,
    remove: removeStore,
  } = useFieldArray({
    control: form.control,
    name: "stores",
  });

  const watchProbabilities = form.watch("probabilities");
  const totalProbability = calculateTotalProbability(watchProbabilities || []);
  const watchCampaignName = form.watch("campaignName");
  const watchStores = form.watch("stores");
  const selectedStoreCodes = (watchStores || [])
    .map((store) => String(store.storeCode))
    .filter(Boolean);
  const allStoresSelected = form.watch("allStoresSelected");
  
  // Identificar lojas existentes (vindas do backend) vs novas
  const existingStoreCodes = useMemo(() => {
    if (!campaign?.stores) return new Set<string>();
    return new Set(campaign.stores.map(store => String(store.storeCode)));
  }, [campaign]);

  const storeOptionsMap = useMemo(
    () => new Map(storeOptions.map((store: IStoreOptionWithDetails) => [store.value, store])),
    [storeOptions]
  );

  const comboboxOptions = useMemo(
    () => storeOptions.map((store: IStoreOptionWithDetails) => ({ value: store.value, label: 'Loja ' + store.value })),
    [storeOptions],
  );

  const filteredComboboxOptions = useMemo(
    () =>
      comboboxOptions.filter(
        (store) => !selectedStoreCodes.includes(String(store.value))
      ),
    [comboboxOptions, selectedStoreCodes]
  );

  const storeOptionsWithAll = allStoresSelected
    ? comboboxOptions
    : [
        {
          value: STORE_OPTION_VALUES.ALL_STORES,
          label: "Todas as Lojas",
        },
        ...filteredComboboxOptions,
      ];
  
  const isCampaignActiveStatus = campaign ? isCampaignActive(campaign.status) : false;
  const isLinkedCampaignEditable = campaign ? isCampaignEditable(campaign.status) : false;

  const {
    banner3x4Preview,
    banner16x9Preview,
    handleBanner3x4Change,
    handleBanner16x9Change,
    removeBanner3x4,
    removeBanner16x9,
  } = useBannerState(form.setValue);

  const { onSubmit } = useFormSubmit({
    campaignId,
    updateCampaign,
    validatePeriod,
  });

  const handleLinkedCampaignSelect = (id: number | null, name: string | null) => {
    form.setValue("linkedCampaignId", id ?? undefined);
    setLinkedCampaignNameOverride(name);
  };

  const handleResetProbabilities = () => {
    form.setValue(
      "probabilities",
      transformProbabilitiesToPercentage(defaultProbabilities)
    );
  };

  const handleAddStore = (storeCode: string) => {
    if (storeCode === STORE_OPTION_VALUES.ALL_STORES) {
      toast({
        title: "Aviso",
        description: "Não é possível selecionar 'Todas as Lojas' ao editar uma campanha",
        variant: "destructive",
      });
      return;
    }

    const store = storeOptionsMap.get(storeCode);
    if (!store) return;

    if (form.getValues("allStoresSelected")) {
      form.setValue("stores", []);
      form.setValue("allStoresSelected", false);
    }

    const storeName = formatStoreAddress(storeCode, storeOptionsMap) || store.label;
    const nickname = formatStoreNickname(storeCode, storeOptionsMap);

    const newStore: IStore = {
      storeCode: store.value,
      storeName,
      nickname,
    };
    appendStore(newStore);
  };

  const handleAddStoresByCodes = () => {
    if (!storeCodesInput.trim()) {
      return;
    }

    if (allStoresSelected) {
      form.setValue("stores", []);
      form.setValue("allStoresSelected", false);
    }

    const codes = Array.from(new Set(
      storeCodesInput
        .split(";")
        .map(code => code.trim())
        .filter(code => code.length > 0)
    ));

    if (codes.length === 0) {
      toast({
        title: "Erro",
        description: "Nenhum código de loja válido foi informado",
        variant: "destructive",
      });
      return;
    }

    const currentStores = form.getValues("stores") || [];
    const existingCodes = currentStores.map(store => String(store.storeCode));
    const newStores: IStore[] = [];
    const notFoundCodes: string[] = [];
    const duplicateCodes: string[] = [];

    codes.forEach(code => {
      if (existingCodes.includes(String(code))) {
        duplicateCodes.push(code);
        return;
      }

      const store = storeOptionsMap.get(code) as IStoreOptionWithDetails | undefined;
      
      if (store) {
        const storeName = formatStoreAddress(code, storeOptionsMap) || store.label;
        const nickname = formatStoreNickname(code, storeOptionsMap);
        newStores.push({
          storeCode: store.value,
          storeName,
          nickname,
        });
        existingCodes.push(String(code));
      } else {
        notFoundCodes.push(code);
      }
    });

    if (newStores.length > 0) {
      newStores.forEach(store => appendStore(store));
      toast({
        title: "Sucesso",
        description: `${newStores.length} loja(s) adicionada(s) com sucesso`,
      });
    }

    if (duplicateCodes.length > 0) {
      const uniqueDuplicates = Array.from(new Set(duplicateCodes));
      toast({
        title: "Aviso",
        description: `Loja(s) ${uniqueDuplicates.join(", ")} já está(ão) na lista e não foi(ram) adicionada(s) novamente`,
        variant: "destructive",
      });
    }

    if (notFoundCodes.length > 0) {
      const uniqueNotFound = Array.from(new Set(notFoundCodes));
      uniqueNotFound.forEach(code => {
        toast({
          title: "Aviso",
          description: `Loja ${code} não encontrada`,
          variant: "destructive",
        });
      });
    }
    setStoreCodesInput("");
  };

  const handleRemoveStore = (index: number) => {
    const store = storeFields[index];
    const storeCode = String(store.storeCode);
    
    // Não permitir remover lojas existentes (vindas do backend)
    if (existingStoreCodes.has(storeCode)) {
      toast({
        title: "Aviso",
        description: "Não é possível remover lojas que já fazem parte da campanha",
        variant: "destructive",
      });
      return;
    }
    
    removeStore(index);
  };

  const isSubmitDisabled =
    isUpdating ||
    probabilityFields.length !== 6 ||
    Math.abs(totalProbability - 100) >= 0.01;

  const getSubmitTitle = () => {
    if (probabilityFields.length !== 6) {
      return "É necessário ter exatamente 6 probabilidades";
    }
    if (Math.abs(totalProbability - 100) >= 0.01) {
      return `A soma das probabilidades deve ser 100%. Atual: ${totalProbability.toFixed(
        2
      )}%`;
    }
    return "";
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Load />
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate("/crm/gestao-campanha-roleta")}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">Erro ao carregar campanha</h1>
        </div>
        <p className="text-red-500">
          Campanha não encontrada ou erro ao carregar dados.
        </p>
      </div>
    );
  }

  return (
    <PageLayout>
      <div className="flex w-full items-center gap-4">
        <Button
          variant="outline"
          size="icon"
          onClick={() => navigate("/crm/gestao-campanha-roleta")}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-bold">Editar Campanha da Roleta</h1>
        <Badge className={getStatusColor(campaign.status)}>
          {getStatusDescription(campaign.status)}
        </Badge>
      </div>

      <form onSubmit={form.handleSubmit(onSubmit)} className="w-full space-y-6">
        <BasicInfoCard
          register={form.register}
          control={form.control}
          errors={form.formState.errors}
          campaignNameLength={watchCampaignName?.length || 0}
          linkedCampaignSearch={linkedCampaignSearch}
          selectedLinkedCampaignName={selectedLinkedCampaignName}
          onLinkedCampaignSelect={handleLinkedCampaignSelect}
          linkedCampaignDisabled={!isLinkedCampaignEditable}
        />

        <StoresCard
          register={form.register}
          storeFields={storeFields}
          storeOptionsWithAll={storeOptionsWithAll}
          allStoresSelected={allStoresSelected || false}
          isLoadingStores={isLoadingStores || isFetchingStores}
          errors={form.formState.errors}
          onAddStore={handleAddStore}
          onRemoveStore={handleRemoveStore}
          storeCodesInput={storeCodesInput}
          setStoreCodesInput={setStoreCodesInput}
          onAddStoresByCodes={handleAddStoresByCodes}
          comboboxOptions={comboboxOptions}
          existingStoreCodes={existingStoreCodes}
          isCampaignActive={isCampaignActiveStatus}
        />

        <ProbabilitiesCard
          register={form.register}
          setValue={form.setValue}
          probabilityFields={probabilityFields}
          appendProbability={appendProbability}
          watchProbabilities={watchProbabilities}
          totalProbability={totalProbability}
          errors={form.formState.errors}
          onResetProbabilities={handleResetProbabilities}
        />

        <BannersCard
          errors={form.formState.errors}
          banner3x4Preview={banner3x4Preview}
          banner16x9Preview={banner16x9Preview}
          hasBanner3x4={campaign.hasBanner3x4 || false}
          hasBanner16x9={campaign.hasBanner16x9 || false}
          onBanner3x4Change={handleBanner3x4Change}
          onBanner16x9Change={handleBanner16x9Change}
          onRemoveBanner3x4={removeBanner3x4}
          onRemoveBanner16x9={removeBanner16x9}
        />

        <RegulationsCard
          setValue={form.setValue}
          errors={form.formState.errors}
          hasRegulationFile={campaign.hasRegulationFile || false}
          regulationsUrl={campaign.regulations}
        />

        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/crm/gestao-campanha-roleta")}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={isSubmitDisabled}
            title={getSubmitTitle()}
          >
            {isUpdating ? "Salvando..." : "Salvar Alterações"}
          </Button>
        </div>
      </form>
    </PageLayout>
  );
};
